import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getSpots,
  getTablets,
  isBasicConfigured,
  makeTabletPayment,
} from "../api/protected";
import { syncTabletsForPosterSpot } from "../api/sync";
import { asArray } from "../poster/response";
import {
  getAccountUrl,
  getPosterToken,
  setCachedPosterUser,
  setPosterToken,
} from "../session";
import { toast } from "react-toastify";
import { MOCK_USER } from "../mocks";
import { InlineLoader, PageLoader } from "../components/Loader";
import {
  formatNextPaymentDate,
  formatStatusLabel,
  formatSubscriptionDate,
  getMonthlyPayWarningText,
  getPlanPriceLines,
  getSubscriptionInfo,
  getTabletRowModifierClass,
  isNextBillingDueOrOverdue,
} from "../utils/tabletVisual";

const IS_DEV = process.env.REACT_APP_DEV === "true";

/**
 * @param {any} t
 * @param {string|undefined} mongoSpotId
 */
function tabletForSpot(t, mongoSpotId) {
  if (!t || !mongoSpotId) return false;
  const s = t.spot_id;
  if (s == null) return false;
  if (typeof s === "object" && s && "_id" in s) {
    return String(/** @type {any} */(s)._id) === String(mongoSpotId);
  }
  return String(s) === String(mongoSpotId);
}

export default function SpotDetailPage() {
  const { spotId } = useParams();
  const accountUrl = getAccountUrl();
  const [list, setList] = useState(/** @type {any[]} */ ([]));
  const [syncing, setSyncing] = useState(false);
  const [backgroundSync, setBackgroundSync] = useState(false);
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState(/** @type {string | null} */ (null));
  const [payingId, setPayingId] = useState(/** @type {string | null} */ (null));

  const loadTabletsFromApi = useCallback(async () => {
    if (!isBasicConfigured() || !String(accountUrl || "").trim() || !spotId) {
      setList([]);
      return;
    }
    const acc = (accountUrl || "").trim();
    const spots = asArray(await getSpots(acc));
    const local = spots.find(
      (d) => d && String(/** @type {any} */(d).spot_id) === String(spotId),
    );
    const mid = local && /** @type {any} */(local)._id
      ? String(/** @type {any} */(local)._id)
      : null;
    if (!mid) {
      setList([]);
      return;
    }
    const all = asArray(await getTablets(acc));
    setList(
      all.filter((t) => tabletForSpot(/** @type {any} */(t), mid)),
    );
  }, [accountUrl, spotId]);

  useEffect(() => {
    let cancelled = false;
    if (IS_DEV && !getPosterToken() && MOCK_USER?.access_token) {
      setPosterToken(String(MOCK_USER.access_token));
      setCachedPosterUser(MOCK_USER);
    }
    (async () => {
      const token = getPosterToken();
      if (!token || !spotId) {
        if (!cancelled) {
          setLoad(false);
        }
        return;
      }
      if (!isBasicConfigured() || !String(getAccountUrl() || "").trim()) {
        if (!cancelled) {
          setList([]);
          setLoad(false);
          if (!IS_DEV) {
            setErr("Нужны Basic и account_url для API");
          }
        }
        return;
      }
      setLoad(true);
      setErr(null);
      try {
        await loadTabletsFromApi();
      } catch (e) {
        if (!cancelled) {
          setErr(/** @type {any} */(e).message || "Ошибка загрузки");
          setList([]);
        }
      } finally {
        if (!cancelled) {
          setLoad(false);
        }
      }
      if (cancelled) {
        return;
      }
      if (!isBasicConfigured() || !String(getAccountUrl() || "").trim()) {
        return;
      }
      setTimeout(() => {
        if (cancelled) {
          return;
        }
        (async () => {
          setBackgroundSync(true);
          const tok = getPosterToken();
          const acc = (getAccountUrl() || "").trim();
          if (!tok || !acc || !spotId) {
            if (!cancelled) {
              setBackgroundSync(false);
            }
            return;
          }
          try {
            await syncTabletsForPosterSpot(tok, acc, spotId);
            if (!cancelled) {
              await loadTabletsFromApi();
            }
          } catch (e) {
            console.warn("фон: синк планшетов", e);
            if (!cancelled) {
              try {
                await loadTabletsFromApi();
              } catch {
                /* */
              }
            }
          } finally {
            if (!cancelled) {
              setBackgroundSync(false);
            }
          }
        })();
      }, 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [spotId, loadTabletsFromApi, accountUrl]);

  const runSync = async () => {
    const token = getPosterToken();
    if (!token) {
      toast.error("Нет сессии Poster");
      return;
    }
    if (!isBasicConfigured() || !String(accountUrl || "").trim()) {
      toast.error("Настройте Basic и account_url");
      return;
    }
    if (!spotId) {
      return;
    }
    setSyncing(true);
    setErr(null);
    try {
      await syncTabletsForPosterSpot(token, accountUrl, spotId);
      await loadTabletsFromApi();
      toast.success("Синхронизировано");
    } catch (e) {
      const msg = /** @type {any} */(e).message;
      setErr(String(msg) || "Ошибка");
      try {
        await loadTabletsFromApi();
      } catch {
        /* */
      }
    } finally {
      setSyncing(false);
    }
  };

  /**
   * @param {any} row
   */
  const handleMakePayment = async (row) => {
    const acc = (getAccountUrl() || "").trim();
    if (!isBasicConfigured() || !acc) {
      toast.error("Настройте Basic и account_url");
      return;
    }
    const tabletMongoId = row?._id != null ? String(row._id) : "";
    if (!tabletMongoId) {
      toast.error("Нет идентификатора планшета");
      return;
    }
    setPayingId(tabletMongoId);
    try {
      await makeTabletPayment(acc, tabletMongoId);
      toast.success("Платёж проведён");
      await loadTabletsFromApi();
    } catch (e) {
      const status = /** @type {any} */(e)?.response?.status;
      const apiMsg = /** @type {any} */(e)?.response?.data?.message;
      if (status === 402 || status === 400) {
        toast.error(String(apiMsg || "Недостаточно средств на балансе"));
      } else if (status === 404) {
        toast.error(String(apiMsg || "Планшет не найден"));
      } else {
        toast.error(String(apiMsg || /** @type {any} */(e)?.message || "Не удалось провести платёж"));
      }
    } finally {
      setPayingId(null);
    }
  };

  if (!getPosterToken() && !IS_DEV) {
    return (
      <section className="bodyContainer bodyContainer--centered-wide">
        <p>Нет токена Poster.</p>
        <Link
          to="/spots"
          className="link-button link-button--sm link-button--secondary"
        >
          К списку точек
        </Link>
      </section>
    );
  }

  if (load) {
    return (
      <section className="bodyContainer bodyContainer--centered-wide">
        <PageLoader />
      </section>
    );
  }

  return (
    <section className="bodyContainer bodyContainer--centered-wide">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <h2 className="settingsPage__title m-0">Кассы, заведение {spotId}</h2>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="link-button link-button--sm d-inline-flex align-items-center justify-content-center"
            style={{ minWidth: "11rem" }}
            onClick={runSync}
            disabled={syncing}
          >
            {syncing ? <InlineLoader label="Синхронизация" /> : "Синхронизировать"}
          </button>
          <Link
            to="/spots"
            className="link-button link-button--sm link-button--secondary"
          >
            Все точки
          </Link>
        </div>
      </div>
      {backgroundSync && isBasicConfigured() && String(accountUrl || "").trim() && (
        <div
          className="d-flex align-items-center mb-2"
          role="status"
          aria-label="Синхронизация с Poster"
        >
          <span className="appSpinner appSpinner--subtle" aria-hidden />
        </div>
      )}
      {err && <div className="alert alert-warning small">{err}</div>}
      <ul className="list-unstyled tabletList">
        {list.length === 0 && (
          <li className="text-muted">
            Нет планшетов в этой точке. Дождитесь фона или нажмите
            «Синхронизировать».
          </li>
        )}
        {list.map((t) => {
          const row = /** @type {any} */(t);
          const rowMap = /** @type {Record<string, unknown>} */(row);
          const mod = getTabletRowModifierClass(rowMap);
          const sub = getSubscriptionInfo(rowMap);
          const planLines = sub?.plan
            ? getPlanPriceLines(sub.plan)
            : /** @type {{ key: string, line: string }[]} */ ([]);
          const monthlyPayWarn = getMonthlyPayWarningText(rowMap);
          const overdue = isNextBillingDueOrOverdue(rowMap);
          const tabletMongoId = row._id != null ? String(row._id) : "";
          const isPaying = payingId === tabletMongoId && tabletMongoId !== "";
          return (
            <li
              key={String(row._id ?? row.tablet_id)}
              className={["tabletRow", mod].filter(Boolean).join(" ")}
            >
              <div className="fw-semibold">
                {row.name || row.tablet_name || "—"}
              </div>
              <div className="tabletRow__meta small">
                ID: {row.tablet_id ?? "—"}
              </div>
              <div className="tabletRow__stack">
                <div className="tabletRow__meta small">
                  Статус: {formatStatusLabel(row.status)}
                </div>
                <div className="tabletRow__meta small">
                  След. списание:{" "}
                  {formatNextPaymentDate(rowMap)}
                </div>
              </div>
              {monthlyPayWarn && (
                <div
                  className="tabletRow__payWarning"
                  role="alert"
                >
                  {monthlyPayWarn}
                </div>
              )}
              {sub && (
                <div className="tabletRow__subscription">
                  <div className="tabletRow__subscriptionHead">Подписка</div>
                  <div className="tabletRow__subscriptionRow small text-muted">
                    <span>Начало: {formatSubscriptionDate(sub.startDate)}</span>
                    <span className="ms-1">
                      · Окончание: {formatSubscriptionDate(sub.endDate)}
                    </span>
                  </div>
                  {sub.plan && Object.keys(sub.plan).length > 0 && (
                    <div className="tabletRow__plan">
                      <div className="tabletRow__planName">
                        {String(sub.plan.name != null && sub.plan.name !== ""
                          ? sub.plan.name
                          : "Тариф")}
                      </div>
                      {planLines.map((p) => (
                        <div
                          key={p.key}
                          className="tabletRow__meta small text-muted"
                        >
                          {p.line}
                        </div>
                      ))}
                    </div>
                  )}
                  {!sub.plan && sub.planId != null && sub.planId !== "" && (
                    <div className="tabletRow__meta small text-muted">
                      ID тарифа: {String(sub.planId)}
                    </div>
                  )}
                </div>
              )}
              {overdue && tabletMongoId && (
                <div className="tabletRow__payActions">
                  <button
                    type="button"
                    className="link-button link-button--sm link-button--secondary tabletRow__payBtn"
                    onClick={() => handleMakePayment(row)}
                    disabled={isPaying}
                  >
                    {isPaying ? <InlineLoader label="Оплата" /> : "Оплатить"}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
